/**
 * Team, People: one person's page (plan docs/plans/lms-plan.md 13 tab 6
 * "a person page: transcript, assignments, waivers, sign-offs, external
 * certificates, onboarding runs, the training log button"; 8.4, 10.5, 18.20,
 * 18.21; Appendix A GET /admin/people/:userId; R16, R17, R18, R22; WP-45).
 *
 * The summary (api/directory.ts getPersonSummary, WP-43's client over WP-32's
 * route) holds everything; the page shows it in tabs:
 *   Transcript     every completion of the network, current, expired and
 *                  superseded, with its certificate (download, or retry a
 *                  pending one; CertificateRow, WP-41)
 *   Assignments    every assignment with why it exists and its state; an open
 *                  one can be waived with a reason, and the newest waived one
 *                  for a course can have its waiver lifted (8.4, 18.16)
 *   Waivers        the waived assignments with their reasons, and Lift
 *   Sign-offs      the observed competency sign-offs (7.4)
 *   External certificates  uploads with their review state and the file (7.5)
 *   Onboarding     the person's onboarding runs (12.4), each opening its run
 *                  page under Team, Onboarding (/lms/team/onboarding/:runId,
 *                  WP-47's route under onboarding/*, plan 18.3)
 * and the Training log (PDF) button (10.5): streamed, saved through a download
 * anchor (api/certificates.ts, WP-41). A leaver stays readable while a grant
 * remains (18.21) and is marked "Left"; someone the admin does not reach is
 * the same 404 as an unknown id.
 */
import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ExternalLink, FileDown, Loader2 } from 'lucide-react';
import Modal from '../../../../ui/Modal';
import SegmentedTabs from '../../../../ui/SegmentedTabs';
import { outlineBtnCls } from '../../../../ui/styles';
import { useToast } from '../../../../ui/Toast';
import { apiErrorMessage } from '../../../api/client';
import { downloadTrainingLog, saveBlob } from '../../../api/certificates';
import { getPersonSummary } from '../../../api/directory';
import type { PersonSummary } from '../../../api/directory';
import { getExternalCertUrl } from '../../../api/externalCerts';
import { CertificateRow } from '../../../components/CertificateRow';
import { openPendingTab } from '../../../components/player/DoneStage';
import { Pill } from '../../../components/StatusPill';
import type { PillTone } from '../../../components/StatusPill';
import {
  cardCls,
  formatDay,
  linkBtnCls,
  metaCls,
  microLabelCls,
  personName,
  roleLabel,
  tableCardCls,
  tableCls,
  tbodyCls,
  tdCls,
  theadCls,
  thCls,
} from '../../../components/pickers/shared';
import { LeaverTag, LoadFailed, Loading, PEOPLE_BASE, TEAM_BASE, useAsync } from '../matrix/shared';
import { UnwaiveForm, WaiveForm } from '../matrix/WaiverForms';

type TabKey = 'transcript' | 'assignments' | 'waivers' | 'signoffs' | 'external' | 'onboarding';

type Row = Record<string, unknown> & { id: string };

function str(v: unknown): string | null {
  return typeof v === 'string' && v ? v : null;
}

function num(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

/** 5.4 lms_assignments.source in words (why it exists, 7.1). */
export const SOURCE_WORDS: Record<string, string> = {
  profile: 'Profile requirement',
  campaign: 'Sent in a campaign',
  renewal: 'Renewal',
  retrain: 'Retrain on a new version',
  acknowledge: 'Acknowledge a change',
  manual: 'Assigned by an admin',
  onboarding: 'Onboarding',
};

/** An assignment's state as a pill: open splits at its due date (8.4). */
export function assignmentState(a: Row, nowMs: number): { word: string; tone: PillTone } {
  const status = str(a.status);
  if (status === 'completed') return { word: 'Completed', tone: 'green' };
  if (status === 'waived') return { word: 'Waived', tone: 'gray' };
  if (status === 'cancelled') return { word: 'Cancelled', tone: 'gray' };
  const due = Date.parse(str(a.due_at) ?? '');
  if (Number.isFinite(due) && nowMs > due) return { word: 'Overdue', tone: 'red' };
  return { word: 'Open', tone: 'primary' };
}

const COMPLETION_STATE: Record<string, { word: string; tone: PillTone }> = {
  current: { word: 'Current', tone: 'green' },
  expired: { word: 'Expired', tone: 'red' },
  superseded: { word: 'Superseded', tone: 'gray' },
};

const COMPLETION_KIND: Record<string, string> = {
  quiz: 'Quiz',
  read_ack: 'Read and acknowledge',
  competency: 'Competency',
  external: 'External certificate',
};

const EXTERNAL_STATE: Record<string, { word: string; tone: PillTone }> = {
  pending: { word: 'Pending review', tone: 'amber' },
  accepted: { word: 'Accepted', tone: 'green' },
  rejected: { word: 'Rejected', tone: 'red' },
};

const RUN_STATE: Record<string, { word: string; tone: PillTone }> = {
  active: { word: 'Active', tone: 'primary' },
  completed: { word: 'Completed', tone: 'green' },
  cancelled: { word: 'Cancelled', tone: 'gray' },
};

/** The ids of the newest assignment per course (by created_at): only that one's waiver can be lifted (8.4). */
function newestPerCourse(assignments: Row[]): Set<string> {
  const best = new Map<string, Row>();
  for (const a of assignments) {
    const courseId = str(a.course_id);
    if (!courseId) continue;
    const seen = best.get(courseId);
    if (!seen || Date.parse(str(a.created_at) ?? '') >= Date.parse(str(seen.created_at) ?? '')) best.set(courseId, a);
  }
  return new Set(Array.from(best.values()).map((a) => a.id));
}

interface WaiverAction {
  kind: 'waive' | 'unwaive';
  assignmentId: string;
  courseTitle: string;
}

function Th({ children }: { children: string }) {
  return (
    <th scope="col" className={thCls}>
      {children}
    </th>
  );
}

function EmptyRow({ span, text }: { span: number; text: string }) {
  return (
    <tr>
      <td className={`${tdCls} text-gray-500 dark:text-gray-400`} colSpan={span}>
        {text}
      </td>
    </tr>
  );
}

export function PersonPage() {
  const { userId = '' } = useParams();
  const { toast } = useToast();
  const [tab, setTab] = useState<TabKey>('transcript');
  const [action, setAction] = useState<WaiverAction | null>(null);
  const [logBusy, setLogBusy] = useState(false);
  const summary = useAsync(() => getPersonSummary(userId), userId, 'This person could not be loaded.', Boolean(userId));
  const person = summary.data;
  const name = person ? personName(person.user) : '';

  const downloadLog = async () => {
    if (logBusy || !person) return;
    setLogBusy(true);
    try {
      const { blob, filename } = await downloadTrainingLog(person.user.id);
      saveBlob(blob, filename);
      toast(`The training log for ${name} was downloaded.`, { type: 'success' });
    } catch (err) {
      toast(apiErrorMessage(err, 'The training log could not be generated. Try again.'), { type: 'error' });
    } finally {
      setLogBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="person-page">
      <Link to={PEOPLE_BASE} className={linkBtnCls} data-testid="person-back">
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        All people
      </Link>

      {summary.error ? <LoadFailed message={summary.error} onRetry={summary.reload} testId="person-error" /> : null}
      {!person && summary.loading ? <Loading rows={4} /> : null}

      {person ? (
        <>
          <PersonHeader person={person} logBusy={logBusy} onLog={() => void downloadLog()} />
          <SegmentedTabs
            label="Person record"
            value={tab}
            onChange={(v) => setTab(v as TabKey)}
            tabs={[
              { value: 'transcript', label: 'Transcript', count: person.completions.length },
              { value: 'assignments', label: 'Assignments', count: person.assignments.length },
              { value: 'waivers', label: 'Waivers', count: person.waivers.length },
              { value: 'signoffs', label: 'Sign-offs', count: person.signoffs.length },
              { value: 'external', label: 'External certificates', count: person.external_certs.length },
              { value: 'onboarding', label: 'Onboarding', count: person.onboarding_runs.length },
            ]}
          />
          {tab === 'transcript' ? <TranscriptPanel person={person} /> : null}
          {tab === 'assignments' ? <AssignmentsPanel person={person} onAction={setAction} /> : null}
          {tab === 'waivers' ? <WaiversPanel person={person} onAction={setAction} /> : null}
          {tab === 'signoffs' ? <SignoffsPanel person={person} /> : null}
          {tab === 'external' ? <ExternalPanel person={person} /> : null}
          {tab === 'onboarding' ? <OnboardingPanel person={person} /> : null}
        </>
      ) : null}

      <Modal
        isOpen={action !== null}
        onClose={() => setAction(null)}
        title={action?.kind === 'unwaive' ? 'Lift the waiver' : 'Waive this requirement'}
        maxWidth="max-w-lg"
      >
        {action && action.kind === 'waive' ? (
          <WaiveForm
            assignmentId={action.assignmentId}
            personName={name}
            courseTitle={action.courseTitle}
            onCancel={() => setAction(null)}
            onDone={() => {
              setAction(null);
              summary.reload();
            }}
          />
        ) : null}
        {action && action.kind === 'unwaive' ? (
          <UnwaiveForm
            assignmentId={action.assignmentId}
            personName={name}
            courseTitle={action.courseTitle}
            onCancel={() => setAction(null)}
            onDone={() => {
              setAction(null);
              summary.reload();
            }}
          />
        ) : null}
      </Modal>
    </div>
  );
}

function PersonHeader({ person, logBusy, onLog }: { person: PersonSummary; logBusy: boolean; onLog: () => void }) {
  const u = person.user;
  return (
    <section className={cardCls} aria-labelledby="person-name">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <h2 id="person-name" className="flex flex-wrap items-center text-base font-semibold text-gray-900 dark:text-gray-100" data-testid="person-name">
            {personName(u)}
            <LeaverTag deleted={u.deleted} />
            {!u.deleted && !u.is_active ? (
              <span className="ml-1.5">
                <Pill tone="gray" dot={false}>
                  Deactivated
                </Pill>
              </span>
            ) : null}
          </h2>
          {u.email ? <p className={metaCls}>{u.email}</p> : null}
          <p className="text-sm text-gray-700 dark:text-gray-200">
            {`${roleLabel(u.role)} \u00b7 ${person.sites.map((s) => s.name ?? 'Unnamed site').join(', ') || 'No site you administer'}`}
          </p>
          {person.groups.length > 0 ? (
            <div className="flex flex-wrap items-center gap-1.5 pt-1" data-testid="person-groups">
              <span className={microLabelCls}>Groups</span>
              {person.groups.map((g) => (
                <Pill key={g.id} tone={g.kind === 'profile' ? 'primary' : 'gray'} dot={false}>
                  {g.kind === 'profile' ? `${g.name} (profile)` : g.name}
                </Pill>
              ))}
            </div>
          ) : null}
        </div>
        <button type="button" className={outlineBtnCls()} onClick={onLog} disabled={logBusy} data-testid="person-training-log">
          {logBusy ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <FileDown className="h-4 w-4" aria-hidden="true" />}
          {logBusy ? 'Generating...' : 'Training log (PDF)'}
        </button>
      </div>
    </section>
  );
}

function TranscriptPanel({ person }: { person: PersonSummary }) {
  return (
    <div className={tableCardCls} data-testid="person-transcript">
      <table className={tableCls}>
        <thead className={theadCls}>
          <tr>
            <Th>Course</Th>
            <Th>Kind</Th>
            <Th>Completed</Th>
            <Th>Expires</Th>
            <Th>Score</Th>
            <Th>State</Th>
            <Th>Certificate</Th>
          </tr>
        </thead>
        <tbody className={tbodyCls}>
          {person.completions.length === 0 ? <EmptyRow span={7} text="No completions yet." /> : null}
          {person.completions.map((c) => {
            const state = COMPLETION_STATE[c.state] ?? { word: c.state, tone: 'gray' as PillTone };
            const kind = str(c.kind) ?? '';
            const score = num(c.score_pct);
            return (
              <tr key={c.id} data-testid={`person-completion-${c.id}`}>
                <td className={tdCls}>{c.course?.title ?? 'Course'}</td>
                <td className={tdCls}>{COMPLETION_KIND[kind] ?? kind}</td>
                <td className={tdCls}>{formatDay(str(c.completed_at))}</td>
                <td className={tdCls}>{str(c.expires_at) ? formatDay(str(c.expires_at)) : 'Does not expire'}</td>
                <td className={`${tdCls} tabular-nums`}>{score === null ? '\u2014' : `${score}%`}</td>
                <td className={tdCls}>
                  <Pill tone={state.tone}>{state.word}</Pill>
                </td>
                <td className={tdCls}>
                  <CertificateRow
                    completionId={c.id}
                    kind={kind}
                    category={c.course?.category ?? null}
                    certificate={c.certificate}
                    courseTitle={c.course?.title ?? null}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function AssignmentsPanel({ person, onAction }: { person: PersonSummary; onAction: (a: WaiverAction) => void }) {
  const newest = useMemo(() => newestPerCourse(person.assignments), [person.assignments]);
  const nowMs = Date.now();
  return (
    <div className={tableCardCls} data-testid="person-assignments">
      <table className={tableCls}>
        <thead className={theadCls}>
          <tr>
            <Th>Course</Th>
            <Th>Why</Th>
            <Th>Due</Th>
            <Th>State</Th>
            <Th>Actions</Th>
          </tr>
        </thead>
        <tbody className={tbodyCls}>
          {person.assignments.length === 0 ? <EmptyRow span={5} text="No assignments." /> : null}
          {person.assignments.map((a) => {
            const state = assignmentState(a, nowMs);
            const title = a.course?.title ?? 'Course';
            const status = str(a.status);
            return (
              <tr key={a.id} data-testid={`person-assignment-${a.id}`}>
                <td className={tdCls}>{title}</td>
                <td className={tdCls}>{SOURCE_WORDS[str(a.source) ?? ''] ?? str(a.source) ?? '\u2014'}</td>
                <td className={tdCls}>{formatDay(str(a.due_at))}</td>
                <td className={tdCls}>
                  <Pill tone={state.tone}>{state.word}</Pill>
                  {status === 'waived' && str(a.waive_reason) ? <div className={metaCls}>{str(a.waive_reason)}</div> : null}
                </td>
                <td className={tdCls}>
                  {status === 'open' ? (
                    <button
                      type="button"
                      className={outlineBtnCls()}
                      onClick={() => onAction({ kind: 'waive', assignmentId: a.id, courseTitle: title })}
                      data-testid={`assignment-waive-${a.id}`}
                    >
                      Waive
                    </button>
                  ) : null}
                  {status === 'waived' && newest.has(a.id) ? (
                    <button
                      type="button"
                      className={outlineBtnCls()}
                      onClick={() => onAction({ kind: 'unwaive', assignmentId: a.id, courseTitle: title })}
                      data-testid={`assignment-unwaive-${a.id}`}
                    >
                      Lift waiver
                    </button>
                  ) : null}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function WaiversPanel({ person, onAction }: { person: PersonSummary; onAction: (a: WaiverAction) => void }) {
  const newest = useMemo(() => newestPerCourse(person.assignments), [person.assignments]);
  return (
    <div className={tableCardCls} data-testid="person-waivers">
      <table className={tableCls}>
        <thead className={theadCls}>
          <tr>
            <Th>Course</Th>
            <Th>Reason</Th>
            <Th>Waived on</Th>
            <Th>Actions</Th>
          </tr>
        </thead>
        <tbody className={tbodyCls}>
          {person.waivers.length === 0 ? <EmptyRow span={4} text="No waivers." /> : null}
          {person.waivers.map((w) => {
            const title = w.course?.title ?? 'Course';
            return (
              <tr key={w.id} data-testid={`person-waiver-${w.id}`}>
                <td className={tdCls}>{title}</td>
                <td className={tdCls}>{str(w.waive_reason) ?? '\u2014'}</td>
                <td className={tdCls}>{formatDay(str(w.waived_at))}</td>
                <td className={tdCls}>
                  {newest.has(w.id) ? (
                    <button
                      type="button"
                      className={outlineBtnCls()}
                      onClick={() => onAction({ kind: 'unwaive', assignmentId: w.id, courseTitle: title })}
                      data-testid={`waiver-unwaive-${w.id}`}
                    >
                      Lift waiver
                    </button>
                  ) : (
                    <span className={metaCls}>A later assignment replaced it</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function SignoffsPanel({ person }: { person: PersonSummary }) {
  return (
    <div className={tableCardCls} data-testid="person-signoffs">
      <table className={tableCls}>
        <thead className={theadCls}>
          <tr>
            <Th>Course</Th>
            <Th>Outcome</Th>
            <Th>Observed</Th>
            <Th>Observer</Th>
            <Th>Notes</Th>
          </tr>
        </thead>
        <tbody className={tbodyCls}>
          {person.signoffs.length === 0 ? <EmptyRow span={5} text="No sign-offs recorded." /> : null}
          {person.signoffs.map((s) => {
            const competent = str(s.outcome) === 'competent';
            return (
              <tr key={s.id} data-testid={`person-signoff-${s.id}`}>
                <td className={tdCls}>{s.course?.title ?? 'Course'}</td>
                <td className={tdCls}>
                  <Pill tone={competent ? 'green' : 'amber'}>{competent ? 'Competent' : 'Not yet'}</Pill>
                </td>
                <td className={tdCls}>{formatDay(str(s.observed_at))}</td>
                <td className={tdCls}>{s.observer_name ?? '\u2014'}</td>
                <td className={tdCls}>{str(s.notes) ?? '\u2014'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ExternalPanel({ person }: { person: PersonSummary }) {
  const { toast } = useToast();
  const [blocked, setBlocked] = useState<{ id: string; url: string } | null>(null);

  const view = async (id: string) => {
    const tab = openPendingTab();
    setBlocked(null);
    try {
      const { url } = await getExternalCertUrl(id, 'inline');
      if (tab && !tab.closed) tab.location.replace(url);
      else setBlocked({ id, url });
    } catch (err) {
      if (tab && !tab.closed) tab.close();
      toast(apiErrorMessage(err, 'The file could not be opened. Try again.'), { type: 'error' });
    }
  };

  return (
    <div className={tableCardCls} data-testid="person-external">
      <table className={tableCls}>
        <thead className={theadCls}>
          <tr>
            <Th>Certificate</Th>
            <Th>Issued</Th>
            <Th>Expires</Th>
            <Th>Requirement</Th>
            <Th>Review</Th>
            <Th>File</Th>
          </tr>
        </thead>
        <tbody className={tbodyCls}>
          {person.external_certs.length === 0 ? <EmptyRow span={6} text="No external certificates uploaded." /> : null}
          {person.external_certs.map((e) => {
            const state = EXTERNAL_STATE[str(e.status) ?? ''] ?? { word: str(e.status) ?? '', tone: 'gray' as PillTone };
            return (
              <tr key={e.id} data-testid={`person-external-${e.id}`}>
                <td className={tdCls}>
                  <div className="font-medium">{str(e.title) ?? 'Certificate'}</div>
                  <div className={metaCls}>{str(e.issuer) ?? ''}</div>
                </td>
                <td className={tdCls}>{formatDay(str(e.issued_on))}</td>
                <td className={tdCls}>{str(e.expires_on) ? formatDay(str(e.expires_on)) : 'Does not expire'}</td>
                <td className={tdCls}>{e.course?.title ?? 'None named'}</td>
                <td className={tdCls}>
                  <Pill tone={state.tone}>{state.word}</Pill>
                  {str(e.review_note) ? <div className={metaCls}>{str(e.review_note)}</div> : null}
                </td>
                <td className={tdCls}>
                  <button type="button" className={outlineBtnCls()} onClick={() => void view(e.id)} data-testid={`person-external-view-${e.id}`}>
                    <ExternalLink className="h-4 w-4" aria-hidden="true" />
                    View
                  </button>
                  {blocked && blocked.id === e.id ? (
                    <a href={blocked.url} target="_blank" rel="noopener noreferrer" className={linkBtnCls}>
                      Open the file
                    </a>
                  ) : null}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function OnboardingPanel({ person }: { person: PersonSummary }) {
  return (
    <div className="space-y-2" data-testid="person-onboarding">
      <div className={tableCardCls}>
        <table className={tableCls}>
          <thead className={theadCls}>
            <tr>
              <Th>Checklist</Th>
              <Th>First day</Th>
              <Th>State</Th>
              <Th>Completed</Th>
            </tr>
          </thead>
          <tbody className={tbodyCls}>
            {person.onboarding_runs.length === 0 ? <EmptyRow span={4} text="No onboarding runs." /> : null}
            {person.onboarding_runs.map((r) => {
              const state = RUN_STATE[str(r.status) ?? ''] ?? { word: str(r.status) ?? '', tone: 'gray' as PillTone };
              return (
                <tr key={r.id} data-testid={`person-run-${r.id}`}>
                  <td className={tdCls}>
                    <Link to={`${TEAM_BASE}/onboarding/${encodeURIComponent(r.id)}`} className={linkBtnCls} data-testid={`person-run-link-${r.id}`}>
                      {r.template_name ?? 'Onboarding checklist'}
                    </Link>
                  </td>
                  <td className={tdCls}>{formatDay(str(r.start_date))}</td>
                  <td className={tdCls}>
                    <Pill tone={state.tone}>{state.word}</Pill>
                  </td>
                  <td className={tdCls}>{str(r.completed_at) ? formatDay(str(r.completed_at)) : '\u2014'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <Link to={`${TEAM_BASE}/onboarding`} className={linkBtnCls}>
        All onboarding runs
      </Link>
    </div>
  );
}

export default PersonPage;
